import { useRef, useEffect, useCallback } from 'react';
import {
  TRACK_COLORS,
  parseSegments,
  computeScreenPoints,
  drawTrack,
} from '../utils/trackRenderer';

// Draw a small race car icon at (cx, cy) with given heading angle and color
function drawCarIcon(ctx, cx, cy, angle, color, size = 12) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);

  // Car body
  ctx.beginPath();
  ctx.moveTo(size, 0);           // nose
  ctx.lineTo(-size * 0.7, -size * 0.5); // rear left
  ctx.lineTo(-size * 0.5, 0);   // rear center indent
  ctx.lineTo(-size * 0.7, size * 0.5);  // rear right
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Windshield
  ctx.beginPath();
  ctx.arc(size * 0.2, 0, size * 0.2, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.fill();

  ctx.restore();

  // Glow ring
  ctx.beginPath();
  ctx.arc(cx, cy, size + 4, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.4;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

export default function TrackCanvas({ coordinates, width = 300, height = 300, carPositions, highlightPoints, focusBounds, mirrorX = false, mirrorY = false }) {
  const canvasRef = useRef(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !coordinates || coordinates.length < 2) return;

    const ctx = canvas.getContext('2d');
    const segments = parseSegments(coordinates);
    const screenPts = computeScreenPoints(coordinates, canvas.width, canvas.height, 40, null, mirrorX, focusBounds, mirrorY);

    drawTrack(ctx, screenPts, segments, 1, {
      shadowWidth: 6,
      lineWidth: 3,
      tickHalfLen: 8,
      checkerSize: 16,
      labelOffset: 14,
      minFontSize: 9,
    });

    // Project GPS coordinates onto canvas for overlays
    const needsGPS = (carPositions && carPositions.length > 0) || (highlightPoints && highlightPoints.length > 0);
    if (needsGPS && coordinates.length > 0) {
      let minLat, maxLat, minLng, maxLng;
      if (focusBounds) {
        ({ minLat, maxLat, minLng, maxLng } = focusBounds);
      } else {
        minLat = Infinity; maxLat = -Infinity; minLng = Infinity; maxLng = -Infinity;
        for (const p of coordinates) {
          if (p.lat < minLat) minLat = p.lat;
          if (p.lat > maxLat) maxLat = p.lat;
          if (p.lng < minLng) minLng = p.lng;
          if (p.lng > maxLng) maxLng = p.lng;
        }
      }
      const latRange = maxLat - minLat || 0.001;
      const lngRange = maxLng - minLng || 0.001;
      const padding = 40;
      const vPad = Math.max(padding, canvas.height * 0.08);
      const availW = canvas.width - padding * 2;
      const availH = canvas.height - vPad * 2;
      const scale = Math.min(availW / lngRange, availH / latRange);
      const cx = canvas.width / 2;
      const cy = canvas.height / 2;
      const midLat = (minLat + maxLat) / 2;
      const midLng = (minLng + maxLng) / 2;

      const projectGPS = (lat, lng) => ({
        x: (mirrorX ? 1 : -1) * (lng - midLng) * scale + cx,
        y: (mirrorY ? -1 : 1) * (lat - midLat) * scale + cy,
      });

      // Draw highlighted segment from drag selection as a continuous line
      if (highlightPoints && highlightPoints.length > 0) {
        ctx.strokeStyle = TRACK_COLORS.zoomHighlight;
        ctx.lineWidth = TRACK_COLORS.zoomHighlightWidth;
        ctx.globalAlpha = TRACK_COLORS.zoomHighlightAlpha;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        for (let i = 0; i < highlightPoints.length; i++) {
          const pos = projectGPS(highlightPoints[i].lat, highlightPoints[i].lng);
          if (i === 0) ctx.moveTo(pos.x, pos.y);
          else ctx.lineTo(pos.x, pos.y);
        }
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Draw car icons at positions
      if (carPositions && carPositions.length > 0) {
        carPositions.forEach((car) => {
          if (car.lat == null || car.lng == null) return;
          const pos = projectGPS(car.lat, car.lng);

          let angle = 0;
          if (car.nextLat != null && car.nextLng != null) {
            const next = projectGPS(car.nextLat, car.nextLng);
            angle = Math.atan2(next.y - pos.y, next.x - pos.x);
          }

          drawCarIcon(ctx, pos.x, pos.y, angle, car.color || '#2e86c1', 10);
        });
      }
    }
  }, [coordinates, carPositions, highlightPoints, focusBounds, mirrorX, mirrorY]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (!coordinates || coordinates.length < 2) {
    return (
      <div style={{
        width, height,
        background: TRACK_COLORS.background,
        borderRadius: 8,
        border: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--color-text-dim)',
        fontFamily: 'var(--font-body)',
        fontSize: 12,
      }}>
        No track data
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        width, height,
        borderRadius: 8,
        border: '1px solid var(--color-border)',
        display: 'block',
      }}
    />
  );
}
